// KidChoresModal — Feb 2026 (Randy). Per-person chore inclusion picker.
// Opens from either a Family invitee row (Chores pill) or a Kid Profile
// row (Chores pill). All chores are CHECKED BY DEFAULT — parent unchecks
// the ones this person can't do. Backend stores the UNCHECKED list as
// `excluded_chore_ids` on the kid_profile / collab_excluded_chores map.
import { useEffect, useMemo, useState } from "react";
import { X, CheckSquare, Square, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useThemeLabel } from "@/lib/CalendarThemeProvider";
import * as T from "@/lib/themeManifest";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function KidChoresModal({ siloId, targetId, targetName, initialExcluded, onClose, onSaved }) {
  // Jun 2026 (Randy) — Theme QA dynamic-word sweep.
  const tChoresBtn    = useThemeLabel(T.THEME_KEY_HOUSEHOLD_CHORES_BUTTON);
  const tTitleSuffix  = useThemeLabel(T.THEME_KEY_KIDMODAL_CHORES_TITLE_SUFFIX);
  const tIntroRaw     = useThemeLabel(T.THEME_KEY_KIDMODAL_CHORES_INTRO);
  const tEmpty        = useThemeLabel(T.THEME_KEY_KIDMODAL_CHORES_EMPTY);
  const tIntro        = tIntroRaw.replace("{name}", targetName || "this person");
  const [chores, setChores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [excluded, setExcluded] = useState(() => new Set(initialExcluded || []));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/calendar/${siloId}/chores`);
        setChores(r.data.chores || []);
      } catch (e) { toast.error("Couldn't load chores."); }
      finally { setLoading(false); }
    })();
  }, [siloId]);

  const activeChores = useMemo(() => chores.filter((c) => c.active), [chores]);
  const allChecked = activeChores.length > 0 && !activeChores.some((c) => excluded.has(c.id));
  const noneChecked = activeChores.every((c) => excluded.has(c.id));

  const toggle = (id) => {
    setExcluded((cur) => {
      const n = new Set(cur); n.has(id) ? n.delete(id) : n.add(id); return n;
    });
  };
  const selectAll = () => setExcluded(new Set());
  const deselectAll = () => setExcluded(new Set(activeChores.map((c) => c.id)));

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/calendar/${siloId}/kid-chores/${targetId}`, {
        excluded_chore_ids: Array.from(excluded),
      });
      toast.success(`Chores updated for ${targetName || "member"}.`);
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FDFCF8] rounded-2xl w-full max-w-md max-h-[90dvh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="kid-chores-modal">
        <div className="px-5 py-3 border-b hairline flex items-center justify-between shrink-0">
          <h2 className="font-serif-display text-lg text-[#1C1B1A]">
            {targetName ? `${targetName}'s ${tTitleSuffix}` : tChoresBtn}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F0F1EE]" data-testid="kid-chores-close"><X size={18} /></button>
        </div>
        <div className="px-5 pt-3 pb-2 shrink-0">
          <p className="text-[11px] text-[#6B6A68] leading-relaxed">
            {tIntro}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <button onClick={selectAll} disabled={allChecked} data-testid="kid-chores-select-all"
              className="text-[10px] px-2 py-1 rounded border hairline hover:border-[#4A5D4E] disabled:opacity-40 flex items-center gap-1">
              <CheckSquare size={11} weight="duotone" /> Select all
            </button>
            <button onClick={deselectAll} disabled={noneChecked} data-testid="kid-chores-deselect-all"
              className="text-[10px] px-2 py-1 rounded border hairline hover:border-[#4A5D4E] disabled:opacity-40 flex items-center gap-1">
              <Square size={11} /> Deselect all
            </button>
            <span className="flex-1" />
            <span className="text-[10px] text-[#6B6A68] tabular-nums" data-testid="kid-chores-count">
              {activeChores.length - excluded.size}/{activeChores.length} allowed
            </span>
          </div>
        </div>
        <div className="px-5 pb-3 flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-xs text-[#6B6A68] italic py-2">Loading…</div>
          ) : activeChores.length === 0 ? (
            <div className="text-[11px] text-[#6B6A68] italic p-3 border hairline rounded-md bg-white/60">
              {tEmpty}
            </div>
          ) : (
            <div className="space-y-1">
              {activeChores.map((c) => {
                const isChecked = !excluded.has(c.id);
                return (
                  <label key={c.id} data-testid={`kid-chores-row-${c.id}`}
                    className={`flex items-center gap-2 rounded px-2.5 py-1.5 border hairline cursor-pointer transition-colors ${isChecked ? "bg-white hover:bg-[#F4F8F5]" : "bg-[#F0EEEA] opacity-60 hover:opacity-80"}`}>
                    <input type="checkbox" checked={isChecked} onChange={() => toggle(c.id)}
                      data-testid={`kid-chores-check-${c.id}`}
                      className="w-4 h-4 accent-[#4A5D4E]" />
                    <span className="text-lg leading-none">{c.emoji}</span>
                    <span className="flex-1 min-w-0">
                      <span className="text-sm text-[#1C1B1A] truncate block">{c.name}</span>
                      <span className="text-[9px] text-[#6B6A68] uppercase tracking-[0.14em]">
                        {c.effort_hint} · {(c.suggested_days || []).length ? c.suggested_days.map((d) => DOW[d]).join("·") : "flexible"} · {c.suggested_time_of_day}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t hairline flex items-center justify-end gap-2 shrink-0 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))]">
          <button onClick={onClose} className="text-xs px-3 py-2 rounded text-[#6B6A68]">Cancel</button>
          <button onClick={save} disabled={busy || loading} data-testid="kid-chores-save"
            className="text-xs px-4 py-2 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
            {busy && <Spinner size={12} className="animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
